import { parseE164 } from './countries';

/**
 * The single shared rule for "is this an SMS-reachable phone number?".
 *
 * It lives next to {@link parseE164} on purpose: the onboarding digest opt-in
 * and Settings → Notifications both collect a number through {@link PhoneInput}
 * and both feed the same SMS digest, so they must agree on what counts as
 * valid. They used to hold a copy each of a shape-only regex
 * (`/^\+[1-9]\d{6,14}$/`), which drifts the moment one side is tightened and
 * happily accepted junk like `+1000000` (YAR-408).
 *
 * Deliberately *not* a full E.164 metadata library: we only need to stop
 * obvious junk before spending a Twilio verification, not to guarantee the
 * number is assigned to a live handset — Twilio's verification code is the
 * real reachability test. So the rule stays country-agnostic and errs on the
 * permissive side for country codes outside our curated catalogue.
 */

/** E.164 caps a full international number at 15 digits, country code included. */
const MAX_E164_DIGITS = 15;

/**
 * Shortest national significant number (NSN) we accept. The shortest mobile
 * NSN across the {@link COUNTRIES} catalogue is 8 digits (Denmark, Norway,
 * Costa Rica, most of Central America); 7 leaves a digit of slack for the odd
 * short landline-style number without letting a 6-digit stub like `+1000000`
 * through.
 */
const MIN_NATIONAL_DIGITS = 7;

/** `+` then a non-zero country-code digit, then digits only — no separators. */
const E164_SHAPE_RE = /^\+[1-9]\d*$/;

/**
 * True when `value` looks like a real, SMS-capable E.164 number.
 *
 * Checks, in order:
 * 1. E.164 shape — `+`, a non-zero leading digit, digits only, ≤ 15 digits.
 * 2. National number length — at least {@link MIN_NATIONAL_DIGITS} digits after
 *    the country code, which is what the old regex could not see: it counted
 *    total digits, so a short country code bought extra slack.
 * 3. No leading zero on the national number — in international format the
 *    national trunk prefix is stripped, so a leading `0` means the user typed
 *    their domestic format (e.g. UK `07911…` → `+4407911…`). Italian landlines
 *    are the textbook exception, but they keep a leading `0` only on fixed
 *    lines, which cannot receive the SMS digest anyway (Italian mobiles start
 *    with `3`).
 * 4. More than one distinct digit — rejects placeholder runs like `000000`,
 *    `1111111111` and `5555555555`, which is the case QA reported.
 */
export function isValidE164Phone(value: string): boolean {
  const candidate = value.trim();
  if (!E164_SHAPE_RE.test(candidate)) return false;
  if (candidate.length - 1 > MAX_E164_DIGITS) return false;

  const { national } = parseE164(candidate);
  if (national.length < MIN_NATIONAL_DIGITS) return false;
  if (national.startsWith('0')) return false;
  if (new Set(national.split('')).size < 2) return false;

  return true;
}
